import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, Gift, AlertCircle, Info } from 'lucide-react';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

const STYLE: Record<string, { icon: any; color: string; bg: string }> = {
  promo: { icon: Gift, color: 'text-pink-500', bg: 'bg-pink-500/10' },
  alert: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
  info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-500/10' },
};

function timeAgo(ts?: string): string {
  if (!ts) return '';
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) { const h = Math.floor(s / 3600); return `${h} hour${h > 1 ? 's' : ''} ago`; }
  if (s < 172800) return 'yesterday';
  return new Date(ts).toLocaleDateString();
}

const Notifications: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch the feed on open. Live delivery of admin-sent notifications now
    // arrives via Web Push (usePushNotifications) rather than a held WebSocket;
    // re-opening this panel refreshes the list.
    const load = async () => {
      const token = localStorage.getItem('accessToken');
      try {
        const r = await fetch(`${API_URL}/notifications/list`, { headers: { Authorization: `Bearer ${token}` } });
        const d = r.ok ? await r.json() : [];
        setItems(Array.isArray(d) ? d : []);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-4 pt-4 pb-24">
      <h2 className="text-white font-bold text-xl mb-4">Notifications</h2>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-safaricab-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <Bell className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/50 text-sm">No notifications yet</p>
          <p className="text-white/30 text-xs mt-1">Offers and updates will show up here.</p>
        </div>
      ) : (
        items.map((n) => {
          const s = STYLE[n.type] || STYLE.info;
          const Icon = s.icon;
          return (
            <div key={n.id} className="flex items-start gap-3 p-4 bg-safaricab-card rounded-xl mb-3 border border-white/10">
              <div className={`w-10 h-10 rounded-full ${s.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-white font-semibold text-sm">{n.title}</p>
                  <span className="text-white/30 text-xs flex-shrink-0">{timeAgo(n.created_at)}</span>
                </div>
                {n.body && <p className="text-white/60 text-xs mt-1">{n.body}</p>}
              </div>
            </div>
          );
        })
      )}
    </motion.div>
  );
};

export default Notifications;
